import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface CoafCommunication {
  id: string;
  tenantId: string;
  pldAlertId?: string;
  type: string;
  status: string;
  operationType: string;
  operationDate: string;
  operationValue: number;
  operationDescription: string;
  involvedParties: InvolvedParty[];
  suspicionIndicators: string[];
  additionalInfo?: string;
  xmlContent?: string;
  pdfUrl?: string;
  submittedAt?: string;
  submittedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  protocolNumber?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvolvedParty {
  type: 'SENDER' | 'RECEIVER' | 'BENEFICIARY' | 'OTHER';
  name: string;
  document: string;
  documentType: 'CPF' | 'CNPJ' | 'PASSPORT' | 'OTHER';
  role: string;
  address?: string;
  phone?: string;
  email?: string;
}

export interface CoafDashboard {
  totalCommunications: number;
  pendingCommunications: number;
  approvedCommunications: number;
  sentCommunications: number;
  rejectedCommunications: number;
  communicationsByType: Record<string, number>;
  communicationsByStatus: Record<string, number>;
  recentCommunications: CoafCommunication[];
  deadlines: CoafDeadline[];
}

export interface CoafDeadline {
  communicationId: string;
  dueDate: string;
  daysRemaining: number;
  status: string;
  type: string;
}

export interface CreateCoafCommunicationDto {
  pldAlertId?: string;
  type: string;
  operationType: string;
  operationDate: string;
  operationValue: number;
  operationDescription: string;
  involvedParties: InvolvedParty[];
  suspicionIndicators: string[];
  additionalInfo?: string;
}

// Hooks
export function useCoafCommunications(query: any = {}) {
  return useQuery({
    queryKey: ['coaf-communications', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([k, v]) => v && params.append(k, String(v)));
      const response = await api.get(`/compliance/coaf/communications?${params.toString()}`);
      return response.data;
    },
  });
}

export function useCoafCommunicationDetail(id: string) {
  return useQuery<CoafCommunication>({
    queryKey: ['coaf-communication', id],
    queryFn: async () => {
      const response = await api.get(`/compliance/coaf/communications/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateCoafCommunication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCoafCommunicationDto) => {
      const response = await api.post('/compliance/coaf/communications', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaf-communications'] });
      queryClient.invalidateQueries({ queryKey: ['coaf-dashboard'] });
    },
  });
}

export function useApproveCoafCommunication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/compliance/coaf/communications/${id}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaf-communications'] });
      queryClient.invalidateQueries({ queryKey: ['coaf-dashboard'] });
    },
  });
}

export function useRejectCoafCommunication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(`/compliance/coaf/communications/${id}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaf-communications'] });
      queryClient.invalidateQueries({ queryKey: ['coaf-dashboard'] });
    },
  });
}

export function useSendCoafCommunication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/compliance/coaf/communications/${id}/send`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaf-communications'] });
      queryClient.invalidateQueries({ queryKey: ['coaf-dashboard'] });
    },
  });
}

export function useExportCoafCommunication() {
  return useMutation({
    mutationFn: async ({ id, format }: { id: string; format: 'xml' | 'pdf' }) => {
      const response = await api.get(`/compliance/coaf/communications/${id}/export?format=${format}`, {
        responseType: 'blob',
      });
      return response.data;
    },
  });
}

export function useCoafDeadlines() {
  return useQuery<CoafDeadline[]>({
    queryKey: ['coaf-deadlines'],
    queryFn: async () => {
      const response = await api.get('/compliance/coaf/deadlines');
      return response.data;
    },
  });
}

export function useCoafDashboard() {
  return useQuery<CoafDashboard>({
    queryKey: ['coaf-dashboard'],
    queryFn: async () => {
      const response = await api.get('/compliance/coaf/dashboard');
      return response.data;
    },
  });
}

// Helpers
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    DRAFT: 'Rascunho',
    PENDING_APPROVAL: 'Aguardando Aprovação',
    APPROVED: 'Aprovada',
    REJECTED: 'Rejeitada',
    SENT: 'Enviada',
    CONFIRMED: 'Confirmada',
  };
  return labels[status] || status;
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-500/20 text-gray-400',
    PENDING_APPROVAL: 'bg-yellow-500/20 text-yellow-400',
    APPROVED: 'bg-blue-500/20 text-blue-400',
    REJECTED: 'bg-red-500/20 text-red-400',
    SENT: 'bg-green-500/20 text-green-400',
    CONFIRMED: 'bg-emerald-500/20 text-emerald-400',
  };
  return colors[status] || 'bg-gray-500/20 text-gray-400';
}

export function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    AUTOMATIC: 'Automática',
    MANUAL: 'Manual',
    COMPLEMENTARY: 'Complementar',
  };
  return labels[type] || type;
}

export function getOperationTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    PIX: 'PIX',
    TED: 'TED',
    DOC: 'DOC',
    BOLETO: 'Boleto',
    CASH_DEPOSIT: 'Depósito em Espécie',
    CASH_WITHDRAWAL: 'Saque em Espécie',
    CRYPTO: 'Criptomoeda',
    OTHER: 'Outro',
  };
  return labels[type] || type;
}

export function getSuspicionIndicatorLabel(indicator: string): string {
  const labels: Record<string, string> = {
    HIGH_VALUE: 'Valor elevado',
    STRUCTURING: 'Fracionamento de operações',
    UNUSUAL_PATTERN: 'Padrão incomum',
    GEOGRAPHIC_RISK: 'Risco geográfico',
    PEP_INVOLVEMENT: 'Envolvimento de PEP',
    SHELL_COMPANY: 'Empresa de fachada',
    RAPID_MOVEMENT: 'Movimentação rápida',
    INCONSISTENT_PROFILE: 'Perfil inconsistente',
    THIRD_PARTY: 'Operação por terceiros',
    OTHER: 'Outro',
  };
  return labels[indicator] || indicator;
}
